import { Group } from '@/components/_common';
import { StoneDevice } from '@/core/domain/stone/devices/devices.types';
import { Badge, Button } from 'antd';
import {
  StoneOrderOriginType,
  StoneOrderStatusType,
} from '@/core/domain/stone/orders/orders.types';
import useStoneHasOrderInfo from '@/hooks/useStoneHasOrderInfo';
import './DeviceItem.styles.less';
import { useMemo } from 'react';

interface Props {
  active: boolean;
  device: StoneDevice;
  companyId?: number;
  onClick: VoidFunction;
}

export default function DeviceItem({
  device,
  active,
  companyId,
  onClick,
}: Props) {
  const { resultData, cancelOrder, isLoading } = useStoneHasOrderInfo({
    isStoneEnabled: !!device.id && !!companyId,
    deviceId: device.id,
  });

  const hasOrderOpen = useMemo(
    () => !!resultData?.data?.hasOpenOrder,
    [resultData],
  );

  const isProcessingOrder = useMemo(
    () =>
      resultData?.data?.hasOpenOrder &&
      resultData?.data?.orderStatus === StoneOrderStatusType.processing_order,
    [resultData],
  );

  const isProcessingCancellation = useMemo(
    () =>
      resultData?.data?.hasOpenOrder &&
      resultData?.data?.orderStatus ===
        StoneOrderStatusType.processing_cancellation,
    [resultData],
  );

  const isProcessing = isProcessingOrder || isProcessingCancellation;

  return (
    <div
      className="device-item-root"
      data-active={active}
      data-isProccess={isProcessing}
      data-disabled={isLoading || !device.active}
      onClick={!isProcessing ? onClick : undefined}
    >
      <Group position="between" gap={24}>
        <Group gap={16}>
          <img src="/images/device-stone.png" alt="Device Stone" width={42} />

          <div>
            <div style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
              {device.serialNumber}
            </div>

            {isLoading && (
              <Group>
                <>
                  <Badge status="processing" />
                  <span>Carregando...</span>
                </>
              </Group>
            )}

            {!isLoading && !device.active && (
              <Group>
                <>
                  <Badge status="default" />
                  <span>Desativado</span>
                </>
              </Group>
            )}

            {!isLoading && !!device.active && (
              <Group>
                {!hasOrderOpen ? (
                  <>
                    <Badge status="success" />
                    <span>Disponível</span>
                  </>
                ) : isProcessingOrder ? (
                  <>
                    <Badge status="processing" />
                    <span>Processando pedido...</span>
                  </>
                ) : (
                  isProcessingCancellation && (
                    <>
                      <Badge status="error" />
                      <span>Processando cancelamento...</span>
                    </>
                  )
                )}
              </Group>
            )}
          </div>
        </Group>

        {resultData?.data?.hasOpenOrder && !isLoading && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            <div style={{ marginBottom: 8 }}>
              {resultData.origin?.originType === StoneOrderOriginType.SALE
                ? `venda direta/${resultData.origin.saleCode}`
                : `orçamento/${String(resultData.origin?.number).padStart(
                    6,
                    '0',
                  )}`}
            </div>

            {resultData?.data?.orderStatus ===
              StoneOrderStatusType.processing_order && (
              <Button
                type="primary"
                danger
                size="small"
                style={{ width: 'auto', paddingLeft: 12, paddingRight: 12 }}
                onClick={cancelOrder}
              >
                Cancelar pedido
              </Button>
            )}
          </div>
        )}
      </Group>
    </div>
  );
}
